// apps/nos/client/src/hooks/useSidebarFlavors.ts
import { useEffect, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  fetchRecentFlavors,
  fetchStarredFlavors,
  searchFlavors,
  starFlavor,
  unstarFlavor,
  type RecentPage,
  type SearchHit,
  type SidebarFlavorRow,
  type StarredFlavorRow,
} from "@/services/flavor_api";

// ────────────────────────────────────────────────────────────────────────
// Query keys
// ────────────────────────────────────────────────────────────────────────

export const flavorKeys = {
  all: ["flavor"] as const,
  recent: ["flavor", "recent"] as const,
  starred: ["flavor", "starred"] as const,
  search: (q: string) => ["flavor", "search", q] as const,
  byId: (id: string) => ["flavor", "byId", id] as const,
};

// ────────────────────────────────────────────────────────────────────────
// useDebouncedValue — co-located helper
// ────────────────────────────────────────────────────────────────────────

export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

// ────────────────────────────────────────────────────────────────────────
// Recent — useInfiniteQuery with maxPages: 3 (90-row sliding window)
// ────────────────────────────────────────────────────────────────────────

type RecentPageParam = { cursor: string | null; direction: "after" | "before" };

export function useRecentFlavors() {
  return useInfiniteQuery({
    queryKey: flavorKeys.recent,
    queryFn: ({ pageParam }: { pageParam: RecentPageParam }) =>
      fetchRecentFlavors(pageParam.cursor, pageParam.direction),
    initialPageParam: { cursor: null, direction: "after" } as RecentPageParam,
    getNextPageParam: (last: RecentPage): RecentPageParam | undefined =>
      last.nextCursor ? { cursor: last.nextCursor, direction: "after" } : undefined,
    getPreviousPageParam: (first: RecentPage): RecentPageParam | undefined =>
      first.previousCursor ? { cursor: first.previousCursor, direction: "before" } : undefined,
    maxPages: 3,
    staleTime: 30_000,
  });
}

// ────────────────────────────────────────────────────────────────────────
// Starred — single-shot
// ────────────────────────────────────────────────────────────────────────

export function useStarredFlavors() {
  return useQuery({
    queryKey: flavorKeys.starred,
    queryFn: fetchStarredFlavors,
    staleTime: 30_000,
  });
}

// ────────────────────────────────────────────────────────────────────────
// Search — debounced
// ────────────────────────────────────────────────────────────────────────

export function useSearchFlavors(q: string, debounceMs = 200) {
  const debounced = useDebouncedValue(q.trim(), debounceMs);
  return useQuery({
    queryKey: flavorKeys.search(debounced),
    queryFn: () => searchFlavors(debounced),
    enabled: debounced.length > 0,
    staleTime: 10_000,
  });
}

// ────────────────────────────────────────────────────────────────────────
// Star toggle — optimistic across Recent / Starred / Search caches
// ────────────────────────────────────────────────────────────────────────

type ToggleArgs = { id: string; next: boolean; flavor: SidebarFlavorRow };

type RecentInfiniteCache = {
  pages: RecentPage[];
  pageParams: unknown[];
};

type StarredCache = { items: StarredFlavorRow[] };

type SearchCache = { items: SearchHit[] };

export function useToggleStar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, next }: ToggleArgs) =>
      next ? starFlavor(id) : unstarFlavor(id),
    onMutate: async ({ id, next, flavor }) => {
      await qc.cancelQueries({ queryKey: flavorKeys.all });

      const prevRecent = qc.getQueryData<RecentInfiniteCache>(flavorKeys.recent);
      const prevStarred = qc.getQueryData<StarredCache>(flavorKeys.starred);

      // Recent: when starring, drop the row from every page. When unstarring,
      // do nothing — onSettled refetch will reinsert it in its correct slot.
      if (next && prevRecent) {
        qc.setQueryData<RecentInfiniteCache>(flavorKeys.recent, {
          ...prevRecent,
          pages: prevRecent.pages.map((p) => ({
            ...p,
            items: p.items.filter((it) => it.id !== id),
          })),
        });
      }

      // Starred: when starring, prepend; when unstarring, drop.
      if (next && prevStarred) {
        const optimistic: StarredFlavorRow = {
          ...flavor,
          starredAt: new Date().toISOString(),
        };
        // Avoid duplicates if the row is somehow already present.
        const filtered = prevStarred.items.filter((it) => it.id !== id);
        qc.setQueryData<StarredCache>(flavorKeys.starred, {
          items: [optimistic, ...filtered],
        });
      } else if (!next && prevStarred) {
        qc.setQueryData<StarredCache>(flavorKeys.starred, {
          items: prevStarred.items.filter((it) => it.id !== id),
        });
      }

      // Search: flip isStarred on every active search query that contains this row.
      const searchQueries = qc.getQueriesData<SearchCache>({ queryKey: ["flavor", "search"] });
      for (const [key, data] of searchQueries) {
        if (!data) continue;
        qc.setQueryData<SearchCache>(key, {
          items: data.items.map((it) =>
            it.id === id ? { ...it, isStarred: next } : it,
          ),
        });
      }

      return { prevRecent, prevStarred, searchSnapshots: searchQueries };
    },
    onError: (_e, _vars, ctx) => {
      if (!ctx) return;
      if (ctx.prevRecent) qc.setQueryData(flavorKeys.recent, ctx.prevRecent);
      if (ctx.prevStarred) qc.setQueryData(flavorKeys.starred, ctx.prevStarred);
      for (const [key, data] of ctx.searchSnapshots) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: flavorKeys.recent });
      qc.invalidateQueries({ queryKey: flavorKeys.starred });
      qc.invalidateQueries({ queryKey: ["flavor", "search"] });
    },
  });
}
